// app/features/templates/components/TemplateDetailDisplayComponent.tsx
"use client";

import {
  BookOpen,
  Briefcase,
  ChevronLeft,
  Code,
  Download,
  FileCode,
  FileJson,
  FileText as FileIcon,
  FileText,
  Lightbulb,
  Lock,
} from "lucide-react";
import Link from "next/link";
import React from "react";

import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook";
import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import { HTFSectionHeader } from "@/app/shared/components/layout/HTFSectionHeader";
import { MarkdownDisplayComponent } from "@/app/shared/components/markdown/MarkdownDisplayComponent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useTemplateDetail } from "../services_and_hooks/useTemplatesHook";
import { TemplateFile } from "../templates.interface";

interface TemplateDetailDisplayProps {
  slug: string;
}

// Helper function to get template category icon
const getCategoryConfig = (categorySlug?: string | null) => {
  switch (categorySlug) {
    case "prompt-techniques":
      return { icon: Lightbulb, label: "Prompt Techniques" };
    case "agent-frameworks":
      return { icon: Code, label: "Agent Frameworks" };
    case "agentic-systems":
      return { icon: Briefcase, label: "Agentic Systems" };
    case "knowledge-management":
      return { icon: BookOpen, label: "Knowledge Management" };
    default:
      return { icon: FileText, label: "Template" };
  }
};

const getIconForFileType = (fileType: string | null | undefined) => {
  // Special case for n8n - return JSX for the logo
  if (fileType === "n8n") {
    const N8nIcon = ({ className }: { className?: string }) => (
      <img
        src="https://enjpbshridmqjvlyelrx.supabase.co/storage/v1/object/public/company-logos/n8n_icon.svg"
        alt="n8n"
        className={cn("w-8 h-8", className)}
      />
    );
    N8nIcon.displayName = "N8nIcon";
    return N8nIcon;
  }

  switch (fileType) {
    case "json":
      return FileJson;
    case "xml":
    case "prompt":
      return FileCode;
    default:
      return FileIcon;
  }
};

export function TemplateDetailDisplayComponent({
  slug,
}: TemplateDetailDisplayProps) {
  const { template, isLoading, error, isDownloading, handleFileDownload } =
    useTemplateDetail(slug);

  const { isAuthenticated, user, hasActiveSubscription } = useAuthHook();

  // Track template viewed
  React.useEffect(() => {
    if (template) {
      (async () => {
        try {
          const { PostHogTrackingService } = await import(
            "@/app/features/posthog/services_and_hooks/posthogTrackingService"
          );
          await PostHogTrackingService.trackTemplateViewed({
            user_id: user?.id,
            email: user?.email,
            template_id: template.id,
            template_slug: slug,
            template_title: template.title,
            template_category: template.template_categories?.slug,
            is_freebie: template.is_freebie || false,
          });
        } catch (error) {
          console.error(
            "[TemplateDetail] Failed to track template viewed:",
            error,
          );
        }
      })();
    }
  }, [template, user, slug]);

  if (isLoading && !template) {
    return <TemplateDetailLoadingSkeleton />;
  }

  if (error) {
    return <TemplateDetailError />;
  }

  if (!template) {
    return <TemplateDetailError />;
  }

  const {
    id: templateId,
    title,
    summary,
    description,
    template_files,
    is_freebie,
    template_tags,
    template_categories,
  } = template;

  const categoryConfig = getCategoryConfig(template_categories?.slug);
  const IconComponent = categoryConfig.icon;

  // Determine if user can download files
  const canDownload = isAuthenticated && (is_freebie || hasActiveSubscription);

  // Content display logic - show preview for non-authenticated users
  const getContentToDisplay = () => {
    if (isAuthenticated) {
      // Authenticated users see full content
      return description ?? "";
    } else {
      // Non-authenticated users see limited preview
      if (description) {
        // Split by lines and take first 4-5 lines
        const lines = description
          .split("\n")
          .filter((line) => line.trim() !== "");
        const previewLines = lines.slice(0, 4); // First 4 non-empty lines

        // Add preview indicator
        const preview = previewLines.join("\n");
        return (
          preview +
          "\n\n---\n\n*🔒 This is just a preview. Sign up to see the complete template and download files.*"
        );
      }
      return summary || "Template preview not available.";
    }
  };

  const displayedContent = getContentToDisplay();

  return (
    <div className="bg-surface animate-fade-in">
      {/* Header */}
      <header
        className={cn(
          "relative py-20 sm:py-24 md:py-32 bg-purple text-black overflow-hidden",
        )}
      >
        {/* <div className="absolute inset-0 bg-grid-pattern-dark opacity-20" /> */}
        {/* <div className="absolute inset-0 bg-gradient-to-t from-purple-700 via-transparent" /> */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex justify-center mb-4">
            <div
              className={cn(
                "w-16 h-16 rounded-xl flex items-center justify-center border-2",
                " border-gray-800",
              )}
            >
              <IconComponent size={32} className="text-gray-800" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-shadow-lg">
            {title}
          </h1>
          {summary && (
            <p className="mt-6 max-w-5xl mx-auto text-lg sm:text-xl text-gray-800">
              {summary}
            </p>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {template_tags?.map(
              (tagAssoc) =>
                tagAssoc.tags && (
                  <Badge
                    key={tagAssoc.tags.slug}
                    variant="outline"
                    className="bg-white/10 border-white/20 text-gray-800 text-sm px-3 py-1"
                  >
                    {tagAssoc.tags.name}
                  </Badge>
                ),
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          {/* Downloads Section - Always visible as preview */}
          {template_files && template_files.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <HTFSectionHeader
                  title="Downloads"
                  icon={Download}
                  divider={false}
                  className="mb-0"
                />

                {/* Download All Button - Conditional functionality */}
                {canDownload ? (
                  <HTFButton
                    variant="secondary-outline"
                    size="default"
                    onClick={() => {
                      // Download all files sequentially
                      template_files.forEach((file, index) => {
                        setTimeout(() => {
                          handleFileDownload(file.id);
                        }, index * 500); // Stagger downloads by 500ms
                      });
                    }}
                    disabled={isDownloading}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download All ({template_files.length})
                  </HTFButton>
                ) : (
                  <HTFButton
                    variant="secondary-outline"
                    size="default"
                    onClick={() => {
                      // Redirect to login/upgrade with current template URL
                      const redirectUrl = isAuthenticated
                        ? "/pricing"
                        : `/login?redirectedFrom=${encodeURIComponent(window.location.pathname)}`;
                      window.location.href = redirectUrl;
                    }}
                    className="flex items-center gap-2"
                  >
                    <Lock className="h-4 w-4" />
                    {isAuthenticated
                      ? "Upgrade to Download"
                      : "Login to Download"}
                  </HTFButton>
                )}
              </div>

              {/* File Cards Row - Always visible but conditional functionality */}
              <div className="flex flex-wrap gap-4">
                {template_files.map((file: TemplateFile) => {
                  if (!file.file_name) return null;
                  const FileTypeIcon = getIconForFileType(file.file_type);

                  return (
                    <div
                      key={file.id}
                      onClick={() => {
                        if (canDownload) {
                          handleFileDownload(file.id);
                        } else {
                          // Redirect to login with current template URL
                          window.location.href = `/login?redirectedFrom=${encodeURIComponent(window.location.pathname)}`;
                        }
                      }}
                      className={cn(
                        "group relative bg-beige-dark rounded-lg p-4 min-w-[140px] max-w-[180px]",
                        "cursor-pointer transition-all duration-200 hover:shadow-lg",
                        "flex flex-col items-center text-center",
                        canDownload
                          ? "hover:border-primary/20"
                          : "hover:border-orange/20",
                        isDownloading &&
                          canDownload &&
                          "opacity-50 cursor-not-allowed",
                      )}
                    >
                      {/* Icon */}
                      <div className="mb-3">
                        <FileTypeIcon
                          className={cn(
                            "h-12 w-12 group-hover:scale-110 transition-transform duration-200",
                            canDownload ? "text-primary" : "text-gray-400",
                          )}
                        />
                      </div>

                      {/* File Name */}
                      <div className="mb-2">
                        <p className="font-medium text-gray-800 text-sm leading-tight line-clamp-2">
                          {file.display_name || file.file_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {file.file_type?.toUpperCase() ?? "FILE"}
                        </p>
                      </div>

                      {/* Hover Indicator */}
                      <div
                        className={cn(
                          "absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center",
                          canDownload ? "bg-primary/5" : "bg-orange/5",
                        )}
                      >
                        <div
                          className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 text-white",
                            canDownload ? "bg-secondary" : "bg-orange",
                          )}
                        >
                          {canDownload ? (
                            <>
                              <Download className="h-3 w-3" />
                              Download
                            </>
                          ) : (
                            <>
                              <Lock className="h-3 w-3" />
                              {isAuthenticated ? "Upgrade" : "Login"}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Loading Overlay */}
                      {isDownloading && canDownload && (
                        <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center">
                          <div className="text-xs text-gray-600">
                            Downloading...
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Login/Upgrade CTA - Moved to right after downloads */}
          {!canDownload && (
            <div id="login-cta" className="my-12">
              <div className="text-center p-8 bg-surface-dark rounded-lg  shadow-inner">
                <h3 className="text-xl font-bold text-gray-800">
                  {!isAuthenticated ? (
                    <>Ready to unlock this template?</>
                  ) : (
                    <>Upgrade to access premium templates</>
                  )}
                </h3>
                <p className="text-gray-600 my-3 max-w-md mx-auto">
                  {!isAuthenticated ? (
                    <>
                      Account access is provisioned for howtheF.ai clients.
                      Contact us if your team needs access.
                    </>
                  ) : (
                    <>
                      Upgrade to Pro to unlock premium templates, advanced
                      workflows, and exclusive content.
                    </>
                  )}
                </p>
                <div className="mt-6 flex justify-center gap-4">
                  {!isAuthenticated ? (
                    <>
                      <HTFButton
                        variant="secondary"
                        onClick={() => {
                          const redirectUrl = `/login?redirectedFrom=${encodeURIComponent(window.location.pathname)}`;
                          console.log(
                            `[TemplateDetail] Redirecting to login: ${redirectUrl}`,
                          );
                          window.location.href = redirectUrl;
                        }}
                      >
                        Log In
                      </HTFButton>
                      <HTFButton
                        variant="primary"
                        onClick={() => {
                          const redirectUrl = `/contact?source=template_access&template=${encodeURIComponent(window.location.pathname)}`;
                          console.log(
                            `[TemplateDetail] Redirecting to contact: ${redirectUrl}`,
                          );
                          window.location.href = redirectUrl;
                        }}
                      >
                        Request Access
                      </HTFButton>
                    </>
                  ) : (
                    <>
                      <Link href="/pricing" passHref>
                        <HTFButton variant="primary">Upgrade to Pro</HTFButton>
                      </Link>
                      <Link href="/templates?freebies=true" passHref>
                        <HTFButton variant="secondary">
                          Browse Free Templates
                        </HTFButton>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content Section */}
          <div className="prose prose-lg max-w-none">
            <MarkdownDisplayComponent markdown={displayedContent} />
          </div>

          {/* Category Section */}
          {template_categories && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <IconComponent size={20} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className="bg-purple-50 border-purple-200 text-purple-700 font-medium"
                >
                  {template_categories.name}
                </Badge>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function TemplateDetailLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-beige-dark space-y-0">
      <div className="bg-purple text-white py-16">
        <div className="container px-4 max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-32 bg-white/20" />
          <Skeleton className="h-16 w-3/4 bg-white/20" />
          <Skeleton className="h-24 w-full bg-white/20" />
        </div>
      </div>
      <div className="bg-beige-dark py-16">
        <div className="container px-4 max-w-6xl mx-auto">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    </div>
  );
}

function TemplateDetailError() {
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
      <h2 className="text-2xl font-bold text-red-600">
        Oops! Something went wrong.
      </h2>
      <p className="mt-4 text-gray-600">
        We couldn't load this template. Please try again later.
      </p>
      <Link href="/templates" passHref>
        <Button variant="outline" className="mt-6">
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Templates
        </Button>
      </Link>
    </div>
  );
}
